import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { fetchDashboardData } from "../../../store/Slices/DashboardSlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import { GetAllItemQtyAdjustmentLogsApi } from "../../../ApiRequests";
import { getAppBasePath, isSuperAdminRole } from "../../../utils/appPaths";
import {
  FaUsers,
  FaStore,
  FaUserTie,
  FaTruck,
  FaWallet,
  FaBuilding,
  FaReceipt,
  FaHandHoldingUsd,
  FaBoxes,
  FaChartLine,
  FaMoneyBill,
} from "react-icons/fa";
import { MdInventory2 } from "react-icons/md";
import { formatDateTime } from "../../../utils/dateTime";

const formatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const toSafeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pickFirstNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const defaultMetricCards = [
  {
    label: "Total Customers",
    value: 1284,
    trend: [40, 45, 43, 48, 55, 58, 62, 70],
    color: "#2563eb",
    icon: FaUsers,
  },
  {
    label: "Shop Type Customers",
    value: 842,
    trend: [24, 29, 31, 30, 33, 35, 40, 44],
    color: "#ea580c",
    icon: FaStore,
  },
  {
    label: "Customer Type Customers",
    value: 442,
    trend: [10, 15, 12, 16, 19, 21, 22, 25],
    color: "#0f766e",
    icon: FaUserTie,
  },
  {
    label: "Total Suppliers",
    value: 116,
    trend: [8, 9, 9, 10, 11, 11, 10, 9],
    color: "#7c3aed",
    icon: FaTruck,
  },
];

const dailySales = [
  { label: "Mon", amount: 52400 },
  { label: "Tue", amount: 49850 },
  { label: "Wed", amount: 56120 },
  { label: "Thu", amount: 61330 },
  { label: "Fri", amount: 64210 },
  { label: "Sat", amount: 58440 },
  { label: "Sun", amount: 47980 },
];
const monthlySales = [
  { label: "Jan", amount: 410320 },
  { label: "Feb", amount: 388940 },
  { label: "Mar", amount: 432510 },
  { label: "Apr", amount: 456880 },
  { label: "May", amount: 472330 },
  { label: "Jun", amount: 489120 },
  { label: "Jul", amount: 501760 },
  { label: "Aug", amount: 518240 },
  { label: "Sep", amount: 503980 },
  { label: "Oct", amount: 547110 },
  { label: "Nov", amount: 563420 },
  { label: "Dec", amount: 589650 },
];
const yearlySales = [
  { label: "2020", amount: 4820000 },
  { label: "2021", amount: 5140500 },
  { label: "2022", amount: 5612300 },
  { label: "2023", amount: 5930700 },
  { label: "2024", amount: 6215400 },
  { label: "2025", amount: 6742200 },
];

const saleTrendConfig = {
  daily: { title: "Daily Sales Trend", subtitle: "Sale Wise", data: dailySales, color: "#155f60" },
  monthly: { title: "Monthly Sales Trend", subtitle: "Sale Wise", data: monthlySales, color: "#0E9F6E" },
  yearly: { title: "Yearly Sales Trend", subtitle: "Sale Wise", data: yearlySales, color: "#b6953a" },
};

const Sparkline = ({ trend, stroke }) => {
  const width = 120;
  const height = 60;
  const max = Math.max(...trend);
  const min = Math.min(...trend);
  const steps = trend.length - 1 || 1;
  const gradientId = `spark-${String(stroke).replace(/[^a-zA-Z0-9]/g, "")}`;
  const points = trend
    .map((value, index) => {
      const x = (index / steps) * width;
      const normalized = max === min ? 0.5 : (value - min) / (max - min);
      const y = height - normalized * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-16" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.4" />
          <stop offset="50%" stopColor={stroke} stopOpacity="0.2" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M0,${height} L${points} L${width},${height} Z`} fill={`url(#${gradientId})`} stroke="none" />
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const MetricCard = ({ label, value, trend, color, icon: Icon, index = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="group relative rounded-2xl bg-card p-4 sm:p-5 shadow-soft hover:shadow-card border border-line flex flex-col gap-3 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.08}s`,
      }}
    >
      <div
        className="absolute top-0 right-0 w-28 h-28 rounded-full opacity-10 blur-3xl transition-opacity duration-500 group-hover:opacity-20"
        style={{ backgroundColor: color }}
      />

      <div className="flex items-start justify-between relative z-10">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-2">
            {label}
          </p>
          <p className="text-2xl font-extrabold text-ink tabular-nums">
            {toSafeNumber(value) >= 1000 ? formatter.format(toSafeNumber(value)) : toSafeNumber(value)}
          </p>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-soft transition-transform duration-300 group-hover:scale-110 shrink-0"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)` }}
        >
          {Icon ? <Icon className="text-lg" /> : null}
        </div>
      </div>
      <div className="relative z-10 opacity-90">
        <Sparkline trend={trend} stroke={color} />
      </div>
    </div>
  );
};

const SalesChart = () => {
  const [range, setRange] = useState("daily");
  const { title, subtitle, data, color } = saleTrendConfig[range];
  const chartHeight = 260;
  const chartWidth = Math.max((data.length - 1 || 1) * 110, 600);
  const maxAmount = Math.max(...data.map((d) => d.amount));
  const minAmount = Math.min(...data.map((d) => d.amount));
  const stepX = chartWidth / (data.length - 1 || 1);
  const normalizeY = (amount) => {
    if (maxAmount === minAmount) return chartHeight / 2;
    const ratio = (amount - minAmount) / (maxAmount - minAmount);
    return chartHeight - ratio * chartHeight;
  };
  const points = data.map((item, index) => `${index * stepX},${normalizeY(item.amount)}`).join(" ");
  const gradientId = `sales-area-${range}`;
  const total = data.reduce((acc, curr) => acc + curr.amount, 0);

  return (
    <div className="rounded-2xl bg-card p-4 sm:p-5 shadow-soft border border-line transition-shadow duration-300">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">{subtitle}</p>
          <h2 className="text-lg font-bold text-ink mt-1 flex items-center gap-2">
            <FaChartLine className="text-main text-base" />
            {title}
          </h2>
        </div>
        <div className="flex items-center gap-1 bg-subtle rounded-lg p-1">
          {Object.keys(saleTrendConfig).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setRange(option)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                range === option ? "bg-card text-main shadow-soft" : "text-inkSecondary hover:text-ink"
              }`}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs flex-wrap bg-subtle p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-muted font-medium">Peak:</span>
          <strong className="text-ink text-base tabular-nums">{formatter.format(maxAmount)}</strong>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted font-medium">Total:</span>
          <strong className="text-ink text-base tabular-nums">{formatter.format(total)}</strong>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted font-medium">Avg:</span>
          <strong className="text-ink text-base tabular-nums">{formatter.format(Math.round(total / data.length))}</strong>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`} className="w-full min-w-[620px] h-80" preserveAspectRatio="none">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`M0,${chartHeight} L${points} L${chartWidth},${chartHeight} Z`} fill={`url(#${gradientId})`} stroke="none" />
          <polyline points={points} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
          {data.map((item, index) => {
            const cx = index * stepX;
            const cy = normalizeY(item.amount);
            return (
              <g key={`${range}-${item.label}`}>
                <circle cx={cx} cy={cy} r="6" fill={color} stroke="white" strokeWidth="2" />
                <text x={cx} y={chartHeight + 25} textAnchor="middle" fill="#8A9496" fontSize="12" fontWeight="600">
                  {item.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const CurrentValueCard = ({ title, value, icon: Icon, index = 0, tone = "neutral" }) => {
  const isAlternate = index % 2 === 1;
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, []);

  const toneIcon =
    tone === "success"
      ? "bg-successSoft text-success"
      : tone === "danger"
      ? "bg-dangerSoft text-danger"
      : tone === "brand"
      ? "bg-brandSoft text-main"
      : "bg-subtle text-inkSecondary";

  return (
    <div
      ref={cardRef}
      className={`group grid grid-cols-[auto_1fr_auto] items-center gap-x-3 px-4 py-3 transition-colors duration-200 hover:bg-subtle ${
        isAlternate ? "bg-subtle/50" : "bg-card"
      }`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateX(0)" : "translateX(-12px)",
        transition: `all 0.4s ease-out ${index * 0.06}s`,
      }}
    >
      {Icon && (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-soft transition-transform duration-300 group-hover:scale-110 ${toneIcon}`}>
          <Icon className="text-base" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-inkSecondary">{title}</p>
      </div>
      <div className="text-right">
        <p className="text-base font-bold text-ink tabular-nums">
          {formatter.format(toSafeNumber(value))} /-
        </p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const DashboardState = useSelector((state) => state.DashboardData);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSuperAdmin = isSuperAdminRole();
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjSummary, setAdjSummary] = useState(null);
  const [adjRows, setAdjRows] = useState([]);

  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      setAdjLoading(true);
      try {
        const res = await GetAllItemQtyAdjustmentLogsApi();
        const payload = res?.data?.data?.payload;
        if (cancelled) return;
        setAdjSummary(payload?.summary || null);
        setAdjRows(
          Array.isArray(payload?.rows) ? payload.rows.slice(0, 12) : []
        );
      } catch {
        if (!cancelled) {
          setAdjSummary(null);
          setAdjRows([]);
        }
      } finally {
        if (!cancelled) setAdjLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  const normalizedData = useMemo(() => {
    const raw = DashboardState?.data;
    if (Array.isArray(raw)) return raw[0] || {};
    if (raw && typeof raw === "object") return raw;
    return {};
  }, [DashboardState?.data]);

  const cards =
    Array.isArray(normalizedData.cards) && normalizedData.cards.length > 0
      ? normalizedData.cards
      : [
          {
            label: "All Customers",
            value: pickFirstNumber(
              normalizedData.customers,
              normalizedData.total_customers,
              normalizedData.total_receivable_count
            ),
            trend: [35, 38, 42, 41, 45, 48, 52, 56],
            color: "#2563eb",
            icon: FaUsers,
          },
          {
            label: "Customers",
            value: pickFirstNumber(
              normalizedData.customer_type_customers,
              normalizedData.customer_customers,
              normalizedData.customers
            ),
            trend: [22, 26, 29, 31, 30, 33, 36, 40],
            color: "#ea580c",
            icon: FaStore,
          },
          {
            label: "Guests",
            value: pickFirstNumber(
              normalizedData.guest_customers,
              normalizedData.shop_type_customers,
              normalizedData.shop_customers
            ),
            trend: [8, 11, 10, 13, 15, 16, 18, 20],
            color: "#0f766e",
            icon: FaUserTie,
          },
          {
            label: "Total Vendors",
            value: pickFirstNumber(
              normalizedData.vendors,
              normalizedData.total_vendors,
              normalizedData.total_payable_count
            ),
            trend: [5, 6, 6, 7, 8, 8, 7, 6],
            color: "#7c3aed",
            icon: FaTruck,
          },
        ];

  const cash = pickFirstNumber(
    normalizedData?.current_value?.cash,
    normalizedData?.current_value?.cash_in_hand,
    normalizedData.cash,
    normalizedData.cash_in_hand,
    normalizedData.total_cash
  );
  const bank = pickFirstNumber(
    normalizedData?.current_value?.bank,
    normalizedData?.current_value?.cash_in_bank,
    normalizedData.bank,
    normalizedData.cash_in_bank,
    normalizedData.total_bank
  );
  const receivable = pickFirstNumber(
    normalizedData?.current_value?.receivable,
    normalizedData.total_receivable,
    normalizedData.receivable
  );
  const payable = pickFirstNumber(
    normalizedData?.current_value?.payable,
    normalizedData.total_payable,
    normalizedData.payable
  );
  const stock = pickFirstNumber(
    normalizedData?.current_value?.stock,
    normalizedData.total_stock,
    normalizedData.stock
  );
  const stockValue = pickFirstNumber(
    normalizedData?.current_value?.stock_value,
    normalizedData.total_stock_value,
    normalizedData.stock_value
  );
  const totalValueFromApi = pickFirstNumber(
    normalizedData?.current_value?.total_value,
    normalizedData.current_value,
    normalizedData.total_value
  );
  const currentValueData = {
    cash,
    bank,
    receivable,
    payable,
    stock,
    stock_value: stockValue,
    total_value: totalValueFromApi || cash + bank + receivable - payable + stock + stockValue,
  };

  return (
    <div className="bg-page flex-1 overflow-y-auto p-4 max-[500px]:px-2">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="bg-card rounded-2xl p-4 sm:p-5 shadow-soft border border-line relative overflow-hidden animate-slideUp">
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-main to-mainHover flex items-center justify-center shadow-soft">
              <FaChartLine className="text-white text-lg" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-ink">Business Snapshot</h1>
              <p className="text-inkSecondary text-xs mt-0.5">
                Daily overview across customers, suppliers, payments, inventory, and sales
              </p>
            </div>
          </div>
        </header>

        {DashboardState.loading ? (
          <div className="w-full flex-1 flex items-center justify-center py-16">
            <FetchingLoading />
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-5 items-start">
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {cards.map((card, idx) => (
                  <MetricCard
                    key={card.label || idx}
                    label={card.label}
                    value={card.value}
                    trend={card.trend || [0, 0, 0, 0, 0, 0, 0, 0]}
                    color={card.color || "#155f60"}
                    icon={card.icon}
                    index={idx}
                  />
                ))}
              </section>

              <section className="grid grid-cols-1 w-full rounded-2xl bg-card shadow-soft border border-line overflow-hidden xl:sticky xl:top-4">
                <div className="p-4 bg-gradient-to-r from-main to-mainHover">
                  <h3 className="text-white font-bold text-base flex items-center gap-2">
                    <FaWallet className="text-lg" />
                    Financial Overview
                  </h3>
                </div>
                <div className="divide-y divide-line">
                  <CurrentValueCard title="Cash-in-Hand" value={toSafeNumber(currentValueData.cash)} icon={FaHandHoldingUsd} index={0} tone="brand" />
                  <CurrentValueCard title="Cash-in-Bank" value={toSafeNumber(currentValueData.bank)} icon={FaBuilding} index={1} tone="brand" />
                  <CurrentValueCard title="Total Receivable" value={toSafeNumber(currentValueData.receivable)} icon={FaReceipt} index={2} tone="success" />
                  <CurrentValueCard title="Total Payable" value={toSafeNumber(currentValueData.payable)} icon={FaReceipt} index={3} tone="danger" />
                  <CurrentValueCard title="Total Stock" value={toSafeNumber(currentValueData.stock)} icon={FaBoxes} index={4} tone="neutral" />
                  <CurrentValueCard title="Total Stock Value" value={toSafeNumber(currentValueData.stock_value)} icon={FaMoneyBill} index={5} tone="neutral" />
                  <CurrentValueCard title="Current Value" value={toSafeNumber(currentValueData.total_value)} icon={FaChartLine} index={6} tone="brand" />
                </div>
              </section>
            </section>

            <section>
              <SalesChart />
            </section>

            {isSuperAdmin && (
              <section className="rounded-2xl bg-card shadow-soft border border-line overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-main to-mainHover flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-white font-bold text-base flex items-center gap-2">
                    <MdInventory2 className="text-lg" />
                    Stock quantity adjustments
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`${getAppBasePath()}/stock-qty-log`)
                    }
                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/15 text-white border border-white/30 hover:bg-white/25 transition-colors"
                  >
                    Full report
                  </button>
                </div>

                {adjLoading ? (
                  <div className="flex justify-center py-10">
                    <FetchingLoading />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border-b border-line bg-subtle/40">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                          Entries
                        </p>
                        <p className="text-lg font-extrabold text-ink tabular-nums">
                          {toSafeNumber(adjSummary?.total_entries)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                          Increased
                        </p>
                        <p className="text-lg font-extrabold text-success tabular-nums">
                          +{toSafeNumber(adjSummary?.increased_qty)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                          Decreased
                        </p>
                        <p className="text-lg font-extrabold text-danger tabular-nums">
                          −{toSafeNumber(adjSummary?.decreased_qty)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                          Net
                        </p>
                        <p className="text-lg font-extrabold text-main tabular-nums">
                          {toSafeNumber(adjSummary?.net_qty)}
                        </p>
                      </div>
                    </div>

                    {adjRows.length === 0 ? (
                      <p className="text-sm text-muted text-center py-8 px-4">
                        No Adjust stock quantity changes logged yet.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-subtle text-inkSecondary text-left">
                              <th className="px-3 py-2 font-semibold">When</th>
                              <th className="px-3 py-2 font-semibold">Item</th>
                              <th className="px-3 py-2 font-semibold text-center">
                                Δ
                              </th>
                              <th className="px-3 py-2 font-semibold">Reason</th>
                              <th className="px-3 py-2 font-semibold">By</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-line">
                            {adjRows.map((row) => (
                              <tr key={row._id} className="hover:bg-subtle/50">
                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                  {formatDateTime(row.createdAt)}
                                </td>
                                <td className="px-3 py-2">
                                  <span className="font-semibold text-ink">
                                    {row.item_name || "—"}
                                  </span>
                                  {row.item_code ? (
                                    <span className="block text-[11px] text-muted">
                                      {row.item_code}
                                    </span>
                                  ) : null}
                                </td>
                                <td
                                  className={`px-3 py-2 text-center font-bold tabular-nums ${
                                    row.delta > 0
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  {row.delta > 0 ? `+${row.delta}` : row.delta}
                                </td>
                                <td className="px-3 py-2 max-w-[220px] truncate">
                                  {row.reason}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                  {row.performedByName || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
